import { cmToFeetInches, kgToLbs } from '@render/utils/unitConversion'
import { formatNumber, type LocaleConfig, type MeasurementSystem } from '@render/lib/localeConfig'
import { localeMonthToken } from '@render/lib/localeMonth'

export type { MeasurementSystem }

export const HEIGHT_WEIGHT_FORMATS = [
  'height_full',
  'height_short',
  'weight_full',
  'weight_short',
  'weight_short_trim',
  'weight_no_label',
] as const

export type HeightWeightFormat = (typeof HEIGHT_WEIGHT_FORMATS)[number]

export function isHeightWeightFormat(
  format: string | null | undefined,
): format is HeightWeightFormat {
  return !!format && (HEIGHT_WEIGHT_FORMATS as readonly string[]).includes(format)
}

// Strip trailing zeros (and a dangling decimal separator) from a formatted
// number — used by the `*_trim` formats. Separator-agnostic so it works for
// locales using `,` as the decimal mark.
function stripTrailingZeros(formatted: string, decimalSep: string): string {
  const idx = formatted.lastIndexOf(decimalSep)
  if (idx === -1) return formatted
  let end = formatted.length
  while (end > idx + 1 && formatted[end - 1] === '0') end--
  if (end === idx + decimalSep.length) end = idx // drop the lone separator
  return formatted.slice(0, end)
}

/**
 * Format a height or weight interpolation. Source value is assumed metric
 * (cm for height, kg for weight). If `system === 'imperial'`, the value is
 * converted to ft+in / lb before being rendered.
 *
 * `decimals` is honored for `weight_*` formats (lets callers render
 * sub-kilogram precision for animated weight interpolation). It is ignored
 * for `height_*` formats — `height_full` renders as a `ft + in` pair in
 * imperial, where fractional feet would be confusing.
 *
 * Config-driven: number separators come from `cfg.number`, and unit labels
 * (short/long for kg/lb/cm/ft/in) come from `cfg.weight` / `cfg.height`, so
 * everything is overridable per (language, geography). See `localeConfig.ts`.
 */
export function formatHeightWeight(
  value: unknown,
  format: HeightWeightFormat,
  system: MeasurementSystem,
  cfg: LocaleConfig,
  decimals: number | null | undefined = null,
): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')

  const weightDigits = decimals ?? 0
  const sep = cfg.number.decimalSeparator

  switch (format) {
    case 'height_full':
      if (system === 'imperial') {
        const { ft, in: inches } = cmToFeetInches(num)
        return `${formatNumber(ft, cfg, 0)} ${cfg.height.foot.long} ${formatNumber(inches, cfg, 0)} ${cfg.height.inch.long}`
      }
      return `${formatNumber(num, cfg, 0)} ${cfg.height.centimeter.long}`

    case 'height_short':
      if (system === 'imperial') {
        const { ft, in: inches } = cmToFeetInches(num)
        return `${formatNumber(ft, cfg, 0)}${cfg.height.foot.short} ${formatNumber(inches, cfg, 0)}${cfg.height.inch.short}`
      }
      return `${formatNumber(num, cfg, 0)}${cfg.height.centimeter.short}`

    case 'weight_full':
      if (system === 'imperial') {
        return `${formatNumber(kgToLbs(num), cfg, weightDigits)} ${cfg.weight.pound.long}`
      }
      return `${formatNumber(num, cfg, weightDigits)} ${cfg.weight.kilogram.long}`

    case 'weight_short':
      if (system === 'imperial') {
        return `${formatNumber(kgToLbs(num), cfg, weightDigits)}${cfg.weight.pound.short}`
      }
      return `${formatNumber(num, cfg, weightDigits)}${cfg.weight.kilogram.short}`

    case 'weight_short_trim':
      if (system === 'imperial') {
        return `${stripTrailingZeros(formatNumber(kgToLbs(num), cfg, weightDigits), sep)}${cfg.weight.pound.short}`
      }
      return `${stripTrailingZeros(formatNumber(num, cfg, weightDigits), sep)}${cfg.weight.kilogram.short}`

    case 'weight_no_label':
      if (system === 'imperial') {
        return formatNumber(kgToLbs(num), cfg, weightDigits)
      }
      return formatNumber(num, cfg, weightDigits)
  }
}

export const DURATION_FORMATS = ['duration_mm_ss'] as const

export type DurationFormat = (typeof DURATION_FORMATS)[number]

export function isDurationFormat(format: string | null | undefined): format is DurationFormat {
  return !!format && (DURATION_FORMATS as readonly string[]).includes(format)
}

export function formatDuration(value: unknown, format: DurationFormat): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  const secs = Math.max(0, Math.floor(num))

  switch (format) {
    case 'duration_mm_ss': {
      const mm = Math.floor(secs / 60)
      const ss = secs % 60
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    }
  }
}

export const PERIOD_FORMATS = ['period_singular', 'period_plural'] as const

export type PeriodFormat = (typeof PERIOD_FORMATS)[number]

export function isPeriodFormat(format: string | null | undefined): format is PeriodFormat {
  return !!format && (PERIOD_FORMATS as readonly string[]).includes(format)
}

/**
 * Format a period-unit interpolation as its translated word. `value` is the
 * unit string ('minute' | 'day' | 'week' | 'month' | 'year'). `resolveTranslation`
 * looks up `tr.global.period.in.<unit>` / `<unit>s` from the pipe store; the
 * `global.` prefix is required because the runtime translations map is keyed
 * by `${group}.${name}` (see PipeFullReleaseDto). Callers pass `resolveTranslation`
 * in because this module stays free of pipe-store coupling (every other
 * formatter here is pure too). Falls back to the raw unit (or unit+'s') when
 * no translation is registered — note that fallback is English-shaped, so for
 * non-English locales the only correct path is the translation hit.
 */
export function formatPeriod(
  value: unknown,
  format: PeriodFormat,
  resolveTranslation: (key: string) => string | undefined,
): string {
  if (value === null || value === undefined || value === '') return ''
  const unit = String(value).toLowerCase()
  const trKey =
    format === 'period_plural' ? `tr.global.period.in.${unit}s` : `tr.global.period.in.${unit}`
  const fallback = format === 'period_plural' ? `${unit}s` : unit
  return resolveTranslation(trKey) ?? fallback
}

export const DATE_FORMATS = ['date_dmy_abbr', 'date_dmy_no_year', 'date_dmy_caps_dash'] as const

export type DateFormat = (typeof DATE_FORMATS)[number]

export function isDateFormat(format: string | null | undefined): format is DateFormat {
  return !!format && (DATE_FORMATS as readonly string[]).includes(format)
}

const DATE_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()
function dateFormatter(locale: string): Intl.DateTimeFormat {
  let f = DATE_FORMATTER_CACHE.get(locale)
  if (!f) {
    // timeZone: 'UTC' so a YYYY-MM-DD seed (parsed as UTC midnight by `new
    // Date()`) doesn't drift by a day in non-UTC display zones — date
    // variables here represent calendar dates, not instants.
    f = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    DATE_FORMATTER_CACHE.set(locale, f)
  }
  return f
}

/**
 * Format a date interpolation as `1 Jan 2026` — capitalised abbreviated
 * month, space-separated, locale-aware month names. Accepts ISO-8601 strings
 * (date or datetime), ms timestamps, Date instances, or anything `new Date()`
 * parses. Returns the raw value as a string when unparseable.
 *
 * Day and year come from the full-date formatter (right numbering system and
 * zero-padding conventions); the month goes through `localeMonthToken`, because
 * the abbreviation a full-date request returns is NUMERIC in cs / sk / ja / lt —
 * `1 6 2026`, month indistinguishable from day. See `localeMonth.ts`.
 */
export function formatDate(value: unknown, format: DateFormat, locale: string = 'en-EN'): string {
  if (value === null || value === undefined || value === '') return ''
  const dt = value instanceof Date ? value : new Date(value as string | number)
  if (isNaN(dt.getTime())) return String(value)

  const parts = dateFormatter(locale).formatToParts(dt)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  // Already trailing-dot-stripped and never lowercased — `Intl` provides the
  // locale's preferred capitalisation.
  const month = localeMonthToken(locale, dt, get('month'))

  switch (format) {
    case 'date_dmy_abbr':
      return `${get('day')} ${month} ${get('year')}`
    case 'date_dmy_no_year':
      return `${get('day')} ${month}`
    case 'date_dmy_caps_dash':
      // `toLocaleUpperCase(locale)` so non-ASCII abbreviations capitalise under
      // the active locale's casing rules (Latvian "jūn" → "JŪN").
      return `${get('day')}-${month.toLocaleUpperCase(locale)}-${get('year')}`
  }
}

export const TEXT_CASE_FORMATS = ['all_caps'] as const

export type TextCaseFormat = (typeof TEXT_CASE_FORMATS)[number]

export function isTextCaseFormat(format: string | null | undefined): format is TextCaseFormat {
  return !!format && (TEXT_CASE_FORMATS as readonly string[]).includes(format)
}

export function formatTextCase(
  value: unknown,
  format: TextCaseFormat,
  locale: string = 'en-EN',
): string {
  if (value === null || value === undefined || value === '') return ''
  switch (format) {
    case 'all_caps':
      return String(value).toLocaleUpperCase(locale)
  }
}
